import { useState, useEffect } from "react";
import { frentistaApi, apiError, formatBRL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { modal } from "@/lib/modal";
import { useWakeLock } from "@/lib/useWakeLock";
import { enqueue as enqueueOffline } from "@/lib/offlineQueue";
import { aditivadaEfetiva, aplicarValoresAjustados, ehSorteio, ehRotaPremio, padraoPermitido } from "@/lib/descontosPista";
import { resolvePadrao, rotaEmAndamento, podeInscreverRota } from "@/lib/rotaFidelizacao";
import { useRecarregaSeVersaoNova } from "@/lib/versaoApp";
import { X, Gift, ArrowRight, Droplets, CheckCircle2 } from "lucide-react";
import {
  PlacaStep, QuatroDigStep, ProductStep, ValueStep, ConfirmStep,
  AverageStep, CouponStep, OleoPremioBanner,
} from "./AtendimentoSteps";
import { NewClientForm, LibEspecialForm, IndicacaoForm } from "./AtendimentoForms";

const STEPS = {
  PLACA: "placa",
  QUATRO_DIG: "quatro_dig",
  PRODUCT: "product",
  VALUE: "value",
  CONFIRM: "confirm",
  AVERAGE: "average",
  COUPON: "coupon",
};

const normPlaca = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
// Fase 3: o atendimento em curso vai junto na consulta da lista de descontos; na Rota de
// Fidelização ele não conta como o abastecimento do dia (ex.: voltar da confirmação para o valor).
const qsAtd = (id) => (id ? `&atendimento_id=${encodeURIComponent(id)}` : "");

export default function AtendimentoFlow() {
  const { frentista } = useAuth();
  useWakeLock(true);
  const [step, setStep] = useState(STEPS.PLACA);
  const [placa, setPlaca] = useState("");
  const [placaData, setPlacaData] = useState(null); // resposta do /otp/por-placa
  const [cliente, setCliente] = useState(null);
  const [quatroDig, setQuatroDig] = useState("");
  const [quatroStatus, setQuatroStatus] = useState(null); // null | "ok" | "erro"
  const [quatroMsg, setQuatroMsg] = useState("");
  const [precos, setPrecos] = useState([]);
  const [descInfo, setDescInfo] = useState(null);
  const [produto, setProduto] = useState(null);
  const [modo, setModo] = useState("reais");
  const [valorInput, setValorInput] = useState("");
  const [calc, setCalc] = useState(null);
  const [chosenDesc, setChosenDesc] = useState(null);
  const [cumularAditivada, setCumularAditivada] = useState(false);
  const [atdId, setAtdId] = useState(null);
  const [finalInfo, setFinalInfo] = useState(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showLibEspecial, setShowLibEspecial] = useState(false);
  const [showIndicacao, setShowIndicacao] = useState(false);
  const [kmAtual, setKmAtual] = useState("");
  const [litrosMedia, setLitrosMedia] = useState("");
  const [mediaResult, setMediaResult] = useState(null);
  const [showAditivada, setShowAditivada] = useState(false);
  const [cashbackInfo, setCashbackInfo] = useState(null);
  const [cupomOleo, setCupomOleo] = useState(null); // aviso de prêmio da troca de óleo atingido
  // v2.28 — resgate do prêmio Troca de Óleo (modal "Utilizar")
  const [showTrocaOleo, setShowTrocaOleo] = useState(false);
  const [oleoValor, setOleoValor] = useState("");
  const [oleoCalc, setOleoCalc] = useState(null);   // preview {valor_sem_desconto, percentual, desconto_reais, valor_final}
  const [oleoDone, setOleoDone] = useState(null);   // resgate confirmado
  // Fase 3: Rota de Fidelização ligada no Master (só consultada para cliente sem abastecimento, que
  // pode ser incluído na Rota pela pista) e inclusão em andamento.
  const [rotaLigada, setRotaLigada] = useState(false);
  const [inscrevendoRota, setInscrevendoRota] = useState(false);
  // Item 2 (19/10): escolhido o prêmio do sorteio, a troca para Aditivada aceita não vale nesse
  // abastecimento (não cumulativo). Fase 3: o mesmo vale para a etapa e o prêmio da Rota, e nas
  // etapas da Rota nada soma. cumularAditivada guarda o aceite; esta é a que vale.
  const cumularEfetivo = aditivadaEfetiva(cumularAditivada, chosenDesc, !!rotaEmAndamento(descInfo));
  // Checagem de versão (revisão das Fases 1 e 2): aba da pista com o JS antigo recarrega sozinha
  // quando há versão nova publicada, só entre um atendimento e outro (tela da placa vazia, sem janela).
  useRecarregaSeVersaoNova(step === STEPS.PLACA && !placa && !showNewClient && !showIndicacao && !showLibEspecial);

  const resetAll = () => {
    setStep(STEPS.PLACA);
    setPlaca(""); setPlacaData(null); setCliente(null);
    setQuatroDig(""); setQuatroStatus(null); setQuatroMsg("");
    setProduto(null);
    setValorInput(""); setCalc(null); setChosenDesc(null); setCumularAditivada(false);
    setAtdId(null); setFinalInfo(null); setKmAtual(""); setLitrosMedia(""); setMediaResult(null);
    setShowAditivada(false); setDescInfo(null); setCashbackInfo(null); setCupomOleo(null);
    setShowTrocaOleo(false); setOleoValor(""); setOleoCalc(null); setOleoDone(null);
    setRotaLigada(false); setInscrevendoRota(false);
  };

  // Fase 3: cliente sem nenhum abastecimento (ex.: autocadastro pela web) e fora da Rota — confere
  // se a Rota está ligada para oferecer a inclusão pela pista. Falha de rede = não oferece.
  const candidatoRota = !!cliente && cliente.total_abastecimentos != null
    && Number(cliente.total_abastecimentos) === 0 && !cliente.rota_status;
  useEffect(() => {
    if (!candidatoRota) { setRotaLigada(false); return undefined; }
    let vivo = true;
    frentistaApi.get(`/master/config-publica?_=${Date.now()}`)
      .then((r) => { if (vivo) setRotaLigada(!!r?.data?.rota_fidelizacao_ativa); })
      .catch(() => { if (vivo) setRotaLigada(false); });
    return () => { vivo = false; };
  }, [candidatoRota, cliente?.id]);

  // Refresh prices+descontos sempre que o vendedor entra (ou volta) à tela de seleção
  // de combustível OU à tela de valor, ou quando a aba volta a ficar visível.
  useEffect(() => {
    if (!cliente || !frentista?.unit_id) return;
    if (step !== STEPS.PRODUCT && step !== STEPS.VALUE) return;
    const refresh = async () => {
      try {
        const cat = cliente.categoria || "normal";
        const prodForDescontos = step === STEPS.VALUE && produto
          ? (cumularEfetivo && produto === "gasolina_comum" ? "gasolina_aditivada" : produto)
          : "gasolina_comum";
        const [precosRes, descRes] = await Promise.all([
          frentistaApi.get(`/precos?unit_id=${frentista.unit_id}&categoria=${cat}&_=${Date.now()}`),
          frentistaApi.get(`/descontos/aplicaveis?cliente_id=${cliente.id}&produto=${prodForDescontos}${qsAtd(atdId)}&_=${Date.now()}`),
        ]);
        setPrecos(precosRes.data);
        setDescInfo(descRes.data);
      } catch { /* mantém estado anterior */ }
    };
    refresh();
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [step, cliente, frentista?.unit_id, produto, cumularEfetivo, atdId]);

  const cancelAttendance = async () => {
    if (atdId) {
      try { await frentistaApi.post(`/atendimentos/${atdId}/cancelar`, {}); } catch { /* ignore */ }
    }
    resetAll();
  };

  // Carrega saldo de cashback do cliente identificado (para o card do QuatroDigStep)
  const loadCashback = async (clienteId) => {
    try {
      const { data } = await frentistaApi.get(`/clientes/${clienteId}/cashback-info?unit_id=${frentista.unit_id}`);
      setCashbackInfo(data);
    } catch { setCashbackInfo(null); }
  };

  // Passo 1: identifica pela placa
  const searchPlaca = async () => {
    const pl = normPlaca(placa);
    if (pl.length < 5) return modal.error("Placa inválida");
    try {
      const { data } = await frentistaApi.post("/otp/por-placa", { placa: pl });
      if (!data.encontrado) {
        setShowNewClient(true); // primeiro acesso
        return;
      }
      setPlacaData(data);
      loadCashback(data.cliente_id);
      // 4 dígitos desativados pelo Master e cadastro liberado → acesso direto pela placa
      if (!data.verificacao_4dig_ativo && !data.bloqueado && !data.limite_atingido) {
        const { data: r } = await frentistaApi.post("/otp/acesso-direto-id", { cliente_id: data.cliente_id, placa: pl });
        await afterVerified(r.cliente);
        return;
      }
      setQuatroDig(""); setQuatroStatus(null); setQuatroMsg("");
      setStep(STEPS.QUATRO_DIG);
    } catch (err) { modal.error(apiError(err)); }
  };

  // Passo 2: valida os 4 últimos dígitos do telefone
  const verify4dig = async () => {
    try {
      const { data } = await frentistaApi.post("/otp/verificar-4dig", {
        cliente_id: placaData.cliente_id, digitos: quatroDig, placa: normPlaca(placa),
      });
      setQuatroStatus("ok");
      await afterVerified(data.cliente);
    } catch (err) {
      setQuatroStatus("erro");
      setQuatroMsg(apiError(err));
      setQuatroDig("");
      // reavalia o estado (pode ter bloqueado após a 3ª tentativa)
      try {
        const { data: pd } = await frentistaApi.post("/otp/por-placa", { placa: normPlaca(placa) });
        if (pd.encontrado) setPlacaData(pd);
      } catch { /* mantém estado */ }
    }
  };

  // Acesso sem celular (por cliente_id — o frentista não vê o telefone completo)
  const accessNoPhone = async () => {
    try {
      const { data } = await frentistaApi.post("/otp/acesso-sem-celular-id", { cliente_id: placaData.cliente_id });
      await afterVerified(data.cliente);
    } catch (err) { modal.error(apiError(err)); }
  };

  const afterVerified = async (cli) => {
    try {
      const { data: atd } = await frentistaApi.post("/atendimentos/iniciar", { cliente_id: cli.id });
      setAtdId(atd.id);
      const [precosRes, descRes] = await Promise.all([
        frentistaApi.get(`/precos?unit_id=${frentista.unit_id}&categoria=${cli.categoria || "normal"}`),
        frentistaApi.get(`/descontos/aplicaveis?cliente_id=${cli.id}&produto=gasolina_comum${qsAtd(atd.id)}`),
      ]);
      setPrecos(precosRes.data);
      setDescInfo(descRes.data);
      setCliente(cli);
      setStep(STEPS.PRODUCT);
    } catch (err) { modal.error(apiError(err)); }
  };

  const selectProduto = async (prod) => {
    try {
      const [precosFresh, descRes] = await Promise.all([
        frentistaApi.get(`/precos?unit_id=${frentista.unit_id}&categoria=${cliente.categoria || "normal"}`),
        frentistaApi.get(`/descontos/aplicaveis?cliente_id=${cliente.id}&produto=${prod}${qsAtd(atdId)}`),
      ]);
      setPrecos(precosFresh.data);
      setDescInfo(descRes.data);
      // "default" pode vir como o item ou só o id (Rota: o item da etapa vem pré-selecionado).
      setChosenDesc(padraoPermitido(resolvePadrao(descRes.data)));
      setProduto(prod);
      setCumularAditivada(false);
      // Nas etapas da Rota (com ou sem desconto hoje) não há oferta de Aditivada.
      if (prod === "gasolina_comum" && descRes.data.aditivada_switch && !rotaEmAndamento(descRes.data)) {
        setShowAditivada(true);
      } else {
        setStep(STEPS.VALUE);
      }
    } catch (err) { modal.error(apiError(err)); }
  };

  const aceitarAditivada = async () => {
    try {
      const [precosFresh, descRes] = await Promise.all([
        frentistaApi.get(`/precos?unit_id=${frentista.unit_id}&categoria=${cliente.categoria || "normal"}`),
        frentistaApi.get(`/descontos/aplicaveis?cliente_id=${cliente.id}&produto=gasolina_aditivada${qsAtd(atdId)}`),
      ]);
      setPrecos(precosFresh.data);
      setDescInfo(descRes.data);
      setChosenDesc(padraoPermitido(resolvePadrao(descRes.data)));
      setCumularAditivada(true);
      setShowAditivada(false);
      setStep(STEPS.VALUE);
    } catch (err) { modal.error(apiError(err)); }
  };

  // Fase 3: inclui na Rota o cliente que ainda não abasteceu (autocadastro pela web). O backend
  // confere tudo de novo (Rota ligada, nenhum abastecimento, frentista); a lista é recarregada para
  // trazer o desconto da Etapa 1.
  const inscreverRota = async () => {
    if (!cliente?.id || inscrevendoRota) return;
    const ok = await modal.confirm({
      title: "Incluir na Rota de Fidelização",
      message: "O cliente entra na Rota agora. Nos próximos 3 abastecimentos (vale 1 por dia) ele recebe só o desconto de cada etapa, sem os outros descontos. Ao completar as 3 etapas ganha o prêmio em saldo para usar no abastecimento seguinte.",
      details: [
        { label: "Cliente", value: cliente.nome || "—" },
        { label: "Placa", value: cliente.placa || normPlaca(placa) || "—" },
      ],
      confirmText: "Incluir na Rota",
    });
    if (!ok) return;
    setInscrevendoRota(true);
    try {
      const { data: insc } = await frentistaApi.post(`/clientes/${cliente.id}/rota/inscrever`, {});
      try {
        const { data } = await frentistaApi.get(
          `/descontos/aplicaveis?cliente_id=${cliente.id}&produto=gasolina_comum${qsAtd(atdId)}&_=${Date.now()}`);
        setDescInfo(data);
      } catch { /* a tela recarrega a lista sozinha ao voltar */ }
      setCliente((c) => (c ? { ...c, rota_status: "ativa" } : c));
      // rota null = a Rota não tem valores nesta unidade/categoria: inscrito, mas sem desconto aqui.
      modal.success(insc && insc.rota === null
        ? "Cliente incluído na Rota de Fidelização. Nesta unidade a Rota ainda não tem valores configurados: por enquanto ele segue os descontos normais."
        : "Cliente incluído na Rota de Fidelização.");
    } catch (err) { modal.error(apiError(err)); }
    finally { setInscrevendoRota(false); }
  };

  // Recarrega o prêmio do cliente (após ganhar/usar) sem mexer no restante do atendimento.
  const refreshPremioOleo = async () => {
    if (!cliente?.id) return;
    try {
      const { data: d } = await frentistaApi.get(
        `/descontos/aplicaveis?cliente_id=${cliente.id}&produto=gasolina_comum${qsAtd(atdId)}&_=${Date.now()}`);
      setDescInfo((prev) => (prev ? { ...prev, oleo_premio: d.oleo_premio } : d));
    } catch { /* mantém o estado atual — nunca esconde o botão por falha de rede */ }
  };

  // v2.28 — resgate do prêmio Troca de Óleo
  const calcTrocaOleo = async () => {
    if (!oleoValor || Number(oleoValor) <= 0) return modal.error("Informe o valor da troca de óleo");
    try {
      const { data } = await frentistaApi.post("/troca-oleo/calcular", {
        cliente_id: cliente.id, valor_troca: Number(oleoValor),
      });
      setOleoCalc(data);
    } catch (err) { modal.error(apiError(err)); }
  };

  const confirmTrocaOleo = async () => {
    try {
      const { data } = await frentistaApi.post("/troca-oleo/confirmar", {
        cliente_id: cliente.id, valor_troca: Number(oleoValor),
      });
      setOleoDone(data);
      // Prêmio consumido → o botão sai só agora (uso efetivado).
      await refreshPremioOleo();
    } catch (err) { modal.error(apiError(err)); }
  };

  const fecharTrocaOleo = () => {
    setShowTrocaOleo(false); setOleoValor(""); setOleoCalc(null); setOleoDone(null);
  };

  const doCalc = async () => {
    if (!valorInput || Number(valorInput) <= 0) return modal.error("Valor inválido");
    try {
      const { data } = await frentistaApi.post("/atendimentos/calcular", {
        atendimento_id: atdId,
        produto,
        modo,
        valor: Number(valorInput),
        desconto_id: chosenDesc?.id || null,
        cumular_aditivada: cumularEfetivo,
      });
      setCalc(data);
      setStep(STEPS.CONFIRM);
    } catch (err) { modal.error(apiError(err)); }
  };

  const confirmSale = async () => {
    try {
      const { data } = await frentistaApi.post(`/atendimentos/${atdId}/confirmar`, {});
      // G6: o prêmio do sorteio deixou de valer no meio do atendimento. Por padrão a venda fica com o
      // desconto e só vem o aviso da ocorrência; com "valores_ajustados" (regra anterior) ela foi
      // finalizada SEM o desconto: a tela e o comprovante passam a mostrar o valor real.
      const ajustado = aplicarValoresAjustados(calc, data);
      if (ajustado !== calc) setCalc(ajustado);
      setFinalInfo(data);
      setStep(STEPS.AVERAGE);
      if (data?.aviso) {
        modal.warning({
          title: ajustado !== calc ? "Cobre o valor sem o desconto" : "Atenção",
          message: String(data.aviso),
          details: ajustado !== calc ? [{ label: "Cobre do cliente", value: formatBRL(ajustado.valor_liquido) }] : undefined,
        });
      }
      // Prêmio troca de óleo: abre o cupom numerado na tela (só p/ PRINT — não vincula a nada).
      if (data.atingiu_oleo && data.cupom_oleo_codigo) setCupomOleo(data.cupom_oleo_codigo);
      // Cliente acabou de ganhar → o botão "Utilizar" passa a aparecer imediatamente.
      if (data.atingiu_oleo) await refreshPremioOleo();
    } catch (err) {
      const isOffline = !navigator.onLine || !err.response;
      if (isOffline) {
        // 4.12: o item leva o token de QUEM FEZ A VENDA; o reenvio usa esse token (troca de turno
        // não descarta mais a venda). Os dados abaixo só servem para o aviso, se algo der errado.
        enqueueOffline({
          url: `/atendimentos/${atdId}/confirmar`,
          method: "POST",
          body: {},
          token: localStorage.getItem("frentista_token"),
          frentista_id: frentista?.id || null,
          meta: {
            atendimento_id: atdId,
            frentista_nome: frentista?.nome || "",
            cliente_nome: cliente?.nome || "",
            placa: normPlaca(placa) || cliente?.placa || "",
            valor_liquido: calc?.valor_liquido ?? null,
          },
        });
        modal.info("Confirmado offline · sincroniza quando voltar online.");
        setFinalInfo({ ok: true, mensagem_cupom: "Salvo offline; sincronizando quando voltar online." });
        setStep(STEPS.AVERAGE);
      } else {
        modal.error(apiError(err));
      }
    }
  };

  const skipOrCalcMedia = async (calcular) => {
    try {
      const body = { atendimento_id: atdId, calcular };
      if (calcular && kmAtual) { body.km_atual = Number(kmAtual); body.litros = Number(litrosMedia) || (calc?.litros || 0); }
      const { data } = await frentistaApi.post(`/atendimentos/${atdId}/media`, body);
      if (calcular) {
        setMediaResult(data.media);
        if (data.media == null) {
          modal.info("Sem KM anterior. Calcularemos no próximo abastecimento.");
        }
      }
      setStep(STEPS.COUPON);
    } catch (err) { modal.error(apiError(err)); }
  };

  // Etapas após o login do cliente: o botão do prêmio fica visível em todas elas.
  const premioOleo = descInfo?.oleo_premio;
  const mostraPremio = !!cliente && !!premioOleo?.disponivel && !showTrocaOleo &&
    [STEPS.PRODUCT, STEPS.VALUE, STEPS.CONFIRM, STEPS.AVERAGE, STEPS.COUPON].includes(step);

  return (
    <div className={`relative min-h-[calc(100vh-72px)] ${mostraPremio ? "pb-28" : ""}`} data-testid="atendimento-flow">
      {mostraPremio && (
        <div className="fixed bottom-4 inset-x-4 z-30 max-w-3xl mx-auto" data-testid="oleo-premio-fixo">
          <OleoPremioBanner premio={premioOleo} onUsar={() => setShowTrocaOleo(true)} />
        </div>
      )}
      {step !== STEPS.PLACA && (
        // Visual aprovado: fixo no canto em todas as larguras. Levar o botão para o fluxo da página
        // no celular depende de aprovação do cliente (revisão da Fase 0).
        <button
          data-testid="cancel-attendance-btn"
          onClick={cancelAttendance}
          className="fixed top-20 right-4 z-40 btn-ghost h-10 px-4 text-xs font-medium flex items-center gap-2 hover:!bg-rose-50 hover:!text-rose-700"
        >
          <X size={13} /> Cancelar
        </button>
      )}

      {step === STEPS.PLACA && (
        <PlacaStep placa={placa} setPlaca={setPlaca} onSearch={searchPlaca}
                   onNew={() => setShowNewClient(true)}
                   onIndicacao={() => setShowIndicacao(true)} />
      )}
      {step === STEPS.QUATRO_DIG && placaData && (
        <QuatroDigStep placaData={placaData} cashbackInfo={cashbackInfo}
                       quatro={quatroDig} setQuatro={(v) => { setQuatroDig(v); setQuatroStatus(null); }}
                       status={quatroStatus} msg={quatroMsg}
                       onVerify={verify4dig} onBack={resetAll}
                       onAccessNoPhone={accessNoPhone} onLibEsp={() => setShowLibEspecial(true)} />
      )}
      {step === STEPS.PRODUCT && (
        <ProductStep cliente={cliente} precos={precos} descInfo={descInfo || {}} onSelect={selectProduto}
                     podeInscreverRota={podeInscreverRota({ rotaLigada, cliente, descInfo })}
                     onInscreverRota={inscreverRota} inscrevendoRota={inscrevendoRota} />
      )}
      {step === STEPS.VALUE && (
        <ValueStep
          cliente={cliente} produto={produto} precos={precos} descInfo={descInfo}
          modo={modo} setModo={setModo} valorInput={valorInput} setValorInput={setValorInput}
          chosenDesc={chosenDesc} setChosenDesc={setChosenDesc} onCalc={doCalc} onBack={() => setStep(STEPS.PRODUCT)}
          cumularAditivada={cumularEfetivo} aditivadaAceita={cumularAditivada}
        />
      )}
      {step === STEPS.CONFIRM && calc && (
        <ConfirmStep calc={calc} onConfirm={confirmSale} onCancel={() => setStep(STEPS.VALUE)} />
      )}
      {step === STEPS.AVERAGE && (
        <AverageStep kmAtual={kmAtual} setKmAtual={setKmAtual} litrosMedia={litrosMedia} setLitrosMedia={setLitrosMedia}
                     calc={calc} ultimoKm={cliente?.ultimo_km} onYes={() => skipOrCalcMedia(true)} onNo={() => skipOrCalcMedia(false)}
                     mediaResult={mediaResult} finalInfo={finalInfo} />
      )}
      {step === STEPS.COUPON && (
        <CouponStep calc={calc} cliente={cliente} finalInfo={finalInfo} mediaResult={mediaResult} onFinish={resetAll} />
      )}

      <Dialog open={showNewClient} onOpenChange={setShowNewClient}>
        {/* Não fecha por clique-fora/ESC: o clique no modal de erro global contava como
            "fora" e fechava a janela no meio da validação do código. Só o X fecha
            (fechamento manual do vendedor = cadastro NÃO criado). */}
        <DialogContent className="max-w-md rounded-2xl border-none p-0 overflow-y-auto"
                       onInteractOutside={(e) => e.preventDefault()}
                       onEscapeKeyDown={(e) => e.preventDefault()}>
          <div className="bg-white p-6 rounded-2xl">
            <NewClientForm initialPlaca={placa} onCreated={async (c) => {
              setShowNewClient(false);
              // Cliente recém-cadastrado está presente no posto → segue direto ao atendimento.
              await afterVerified(c);
            }} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showLibEspecial} onOpenChange={setShowLibEspecial}>
        <DialogContent className="max-w-sm rounded-2xl border-none p-0 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl">
            <LibEspecialForm clienteId={placaData?.cliente_id} clienteNome={placaData?.nome} onSuccess={async (c) => {
              setShowLibEspecial(false);
              await afterVerified(c);
            }} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showIndicacao} onOpenChange={setShowIndicacao}>
        <DialogContent className="max-w-md rounded-2xl border-none p-0 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl">
            <IndicacaoForm onClose={() => setShowIndicacao(false)} />
          </div>
        </DialogContent>
      </Dialog>

      {/* v2.28 — RESGATE do prêmio Troca de Óleo (botão "Utilizar") */}
      <Dialog open={showTrocaOleo} onOpenChange={(v) => !v && fecharTrocaOleo()}>
        <DialogContent className="max-w-sm rounded-2xl border-none p-0 overflow-y-auto"
                       onInteractOutside={(e) => e.preventDefault()}>
          <div className="bg-white p-6 rounded-2xl space-y-4" data-testid="troca-oleo-modal">
            {!oleoDone ? (
              <>
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <Droplets className="text-white" size={16} />
                  </div>
                  <div>
                    <h2 className="font-serif-display text-2xl leading-none">Usar prêmio Troca de Óleo</h2>
                    <div className="text-[11px] text-neutral-500 mt-1">{cliente?.nome}</div>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
                    Valor da troca de óleo (R$)
                  </label>
                  <input data-testid="troca-oleo-valor" className="digit-input w-full mt-1" inputMode="decimal"
                         placeholder="0,00" value={oleoValor}
                         onChange={(e) => { setOleoValor(e.target.value.replace(",", ".")); setOleoCalc(null); }} />
                </div>
                {oleoCalc && (
                  <div data-testid="troca-oleo-preview" className="rounded-xl border border-neutral-200 divide-y divide-neutral-100">
                    <div className="flex items-center justify-between px-3 py-2 text-[13px]">
                      <span className="text-neutral-500">Valor sem desconto</span>
                      <b>{formatBRL(oleoCalc.valor_sem_desconto)}</b>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2 text-[13px]">
                      <span className="text-neutral-500">Desconto aplicado ({Number(oleoCalc.percentual).toLocaleString("pt-BR")}%)</span>
                      <b className="text-rose-600">− {formatBRL(oleoCalc.desconto_reais)}</b>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2.5"
                         style={{ background: "linear-gradient(135deg,#FEF3C7 0%,#FDE68A 100%)" }}>
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-neutral-700">Cliente paga</span>
                      <span data-testid="troca-oleo-final" className="font-serif-display text-2xl">{formatBRL(oleoCalc.valor_final)}</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <button onClick={fecharTrocaOleo} className="btn-ghost h-11 text-sm font-medium">Cancelar</button>
                  {!oleoCalc ? (
                    <button data-testid="troca-oleo-calcular" onClick={calcTrocaOleo}
                            className="btn-primary-blue h-11 text-sm font-medium">Calcular</button>
                  ) : (
                    <button data-testid="troca-oleo-confirmar" onClick={confirmTrocaOleo}
                            className="btn-accent h-11 text-sm font-medium">Confirmar desconto</button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center space-y-3" data-testid="troca-oleo-done">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-emerald-600" />
                </div>
                <h2 className="font-serif-display text-2xl leading-tight">Desconto aplicado</h2>
                <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-100 text-left">
                  <div className="flex items-center justify-between px-3 py-2 text-[13px]">
                    <span className="text-neutral-500">Troca de óleo</span><b>{formatBRL(oleoDone.valor_sem_desconto)}</b>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2 text-[13px]">
                    <span className="text-neutral-500">Desconto ({Number(oleoDone.percentual).toLocaleString("pt-BR")}%)</span>
                    <b className="text-rose-600">− {formatBRL(oleoDone.desconto_reais)}</b>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2.5 bg-emerald-50">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-neutral-700">Cobre do cliente</span>
                    <span className="font-serif-display text-2xl">{formatBRL(oleoDone.valor_final)}</span>
                  </div>
                </div>
                <p className="text-[11px] text-neutral-500">
                  O desconto de {formatBRL(oleoDone.desconto_reais)} já entrou no seu caixa.
                </p>
                <button data-testid="troca-oleo-fechar" onClick={fecharTrocaOleo}
                        className="w-full h-11 btn-primary-blue text-sm font-medium">Concluir</button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cupom TROCA DE ÓLEO — aviso na tela com código numerado, só para print (não vincula a nada) */}
      <Dialog open={!!cupomOleo} onOpenChange={(v) => !v && setCupomOleo(null)}>
        <DialogContent className="max-w-sm rounded-2xl border-none p-0 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl space-y-3 text-center" data-testid="cupom-oleo">
            <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#FED7AA 0%,#FDBA74 100%)" }}>
              <Droplets size={26} className="text-orange-700" />
            </div>
            <h2 className="font-serif-display text-2xl leading-tight">Prêmio Troca de Óleo!</h2>
            <p className="text-[12px] text-neutral-600 leading-snug">
              O cliente <b>{cliente?.nome}</b> atingiu o prêmio da troca de óleo.
            </p>
            <div className="rounded-xl border-2 border-dashed border-orange-300 bg-orange-50/60 py-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-orange-700 font-semibold">Cupom Nº</div>
              <div data-testid="cupom-oleo-codigo" className="font-mono text-4xl font-bold tracking-[6px] text-orange-800 mt-1">
                {cupomOleo}
              </div>
              <div className="text-[10px] text-neutral-500 mt-2">{new Date().toLocaleString("pt-BR")}</div>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[11px] text-neutral-500">
              <Gift size={12} /> Prêmio <b>registrado no cadastro do cliente</b> — para usar, toque em <b>Utilizar</b> na barra da Troca de Óleo.
            </div>
            <button data-testid="cupom-oleo-fechar" onClick={() => setCupomOleo(null)}
                    className="w-full h-11 btn-primary-blue text-sm font-medium">
              Continuar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAditivada} onOpenChange={setShowAditivada}>
        <DialogContent className="max-w-sm rounded-2xl border-none p-0 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl space-y-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                <Gift className="text-white" size={16} />
              </div>
              <h2 className="font-serif-display text-2xl">Oferta Aditivada</h2>
            </div>
            <p className="text-sm text-neutral-600">
              Troque para <b>Gasolina Aditivada</b> com desconto adicional de <b>R$ 0,05/L</b> sobre o preço de bomba.
              Os outros descontos do cliente continuam aplicáveis (cumulativo).
            </p>
            {(descInfo?.descontos || []).some(ehSorteio) && (
              <p data-testid="aditivada-aviso-sorteio" className="text-[12px] text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                O cliente tem <b>prêmio do sorteio</b>, que não soma com a Aditivada: se o prêmio for escolhido, a troca não vale neste abastecimento.
              </p>
            )}
            {(descInfo?.descontos || []).some(ehRotaPremio) && (
              <p data-testid="aditivada-aviso-rota-premio" className="text-[12px] text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                O cliente tem o <b>prêmio da Rota de Fidelização</b>, que não soma com a Aditivada: se o prêmio for escolhido, a troca não vale neste abastecimento.
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button data-testid="aditivada-decline" onClick={() => { setShowAditivada(false); setStep(STEPS.VALUE); }}
                      className="btn-ghost h-11 text-sm font-medium">Manter Comum</button>
              <button data-testid="aditivada-accept" onClick={aceitarAditivada}
                      className="btn-accent h-11 text-sm font-medium flex items-center justify-center gap-1.5">
                Receber Desconto <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
